/**
 * Things you decide about other people, that only you see.
 *
 * A nickname here renames somebody everywhere in your client and nowhere on
 * the server - they are never told, and nobody else sees it. That is the whole
 * point: it is for telling two "xX_Sniper_Xx"s apart in a call, not for
 * labelling anyone publicly.
 *
 * Kept out of the account settings deliberately. This is per-device, survives
 * a reload, and never leaves the machine.
 */

import { create } from 'zustand';

const KEY = 'pogu.nicknames';

function read(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '{}'); }
  catch { return {}; }
}

type LocalState = {
  nicknames: Record<string, string>;
  /** An empty name clears it and gives the person their own name back. */
  setNickname: (userId: string, name: string) => void;

  /**
   * Whether this person wants to see crew cosmetics at all.
   *
   * On by default. Off draws every crew tag as a plain [PUA], skips the
   * decorative layers entirely, and stops the hover motion - and it is a
   * viewing preference, so it changes nothing for anybody else and does not
   * touch what the crew has chosen.
   *
   * Per device and read synchronously at startup, so the first paint is
   * already right rather than flashing the decorated version first.
   */
  crewCosmetics: boolean;
  setCrewCosmetics: (on: boolean) => void;

  /** The small lift when you point at a tag. Reduced motion overrides it. */
  crewTagMotion: boolean;
  setCrewTagMotion: (on: boolean) => void;
};

const COSMETICS_KEY = 'pogu.crewCosmetics';
const MOTION_KEY = 'pogu.crewTagMotion';

/** Defaults to on: a missing key is a new install, not a refusal. */
function flag(key: string): boolean {
  try { return localStorage.getItem(key) !== '0'; } catch { return true; }
}

export const useLocal = create<LocalState>((set, get) => ({
  nicknames: read(),
  crewCosmetics: flag(COSMETICS_KEY),
  crewTagMotion: flag(MOTION_KEY),
  setCrewCosmetics(on) {
    set({ crewCosmetics: on });
    try { localStorage.setItem(COSMETICS_KEY, on ? '1' : '0'); } catch { /* storage off */ }
  },
  setCrewTagMotion(on) {
    set({ crewTagMotion: on });
    try { localStorage.setItem(MOTION_KEY, on ? '1' : '0'); } catch { /* storage off */ }
  },
  setNickname(userId, name) {
    const next = { ...get().nicknames };
    const trimmed = name.trim().slice(0, 32);
    if (trimmed) next[userId] = trimmed;
    else delete next[userId];
    set({ nicknames: next });
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage off */ }
  }
}));

/** What to call somebody, taking your own nickname for them into account. */
export function displayNameOf(user: { id: string; displayName: string }): string {
  return useLocal.getState().nicknames[user.id] || user.displayName;
}
